/**
 * The signed-in client's own generations, plus their retention preference.
 *
 * Ported from `app/history/page.tsx`. **Most of that file was a hand-rolled state machine** —
 * `generations: T[] | null` to distinguish loading from empty, a separate error string, a
 * `retentionSaving` boolean, a `load()` callback re-invoked after every mutation — and React
 * Query owns all of it here. What is left is the behaviour that is actually about history:
 *
 * - The two requests load together, because the dropdown needs its current value at the same
 *   moment the list needs its contents.
 * - **Changing the retention period reloads the list**, rather than only updating the
 *   dropdown. The save has already swept anything past the new, shorter window server-side,
 *   and showing images the server has just deleted would be worse than a brief spinner. That
 *   is the invalidation in `useSetRetentionPeriod`.
 * - A 403 is a permanent state, not a failure to retry — `QueryError` decides that centrally.
 *
 * The cards themselves are `GenerationCard`, shared with `/admin/history` for the reason the
 * Next.js app shared `HistoryItem`: the two views differ by one label, and two copies would
 * drift.
 */

import {
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import type { RetentionPeriod } from "@/api/queries";
import {
  useDeleteGeneration,
  useHistory,
  useRetentionPeriod,
  useSetRetentionPeriod,
} from "@/api/queries";
import { GenerationCard } from "@/components/GenerationCard";
import { PageHeading } from "@/components/PageHeading";
import { CentredSpinner, QueryError } from "@/components/RequireAuth";
import { RETENTION_PERIOD_OPTIONS } from "@/constants/retention";
import { RADIUS_INNER_PX } from "@/theme/tokens";

export function HistoryPage() {
  const history = useHistory();
  const retention = useRetentionPeriod();
  const setRetention = useSetRetentionPeriod();
  const deleteGeneration = useDeleteGeneration();

  return (
    <Stack spacing={4}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <PageHeading overline="Your work" title="History" />

        <RetentionControl
          value={retention.data?.retentionPeriod}
          disabled={retention.isPending || setRetention.isPending}
          onChange={(period) => setRetention.mutate(period)}
        />
      </Stack>

      {setRetention.isError ? <QueryError error={setRetention.error} /> : null}

      {history.isPending ? (
        <CentredSpinner label="Loading your history…" />
      ) : history.isError ? (
        <QueryError error={history.error} />
      ) : history.data.generations.length === 0 ? (
        <EmptyHistory />
      ) : (
        <Box className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {history.data.generations.map((generation) => (
            <GenerationCard
              key={generation.id}
              generation={generation}
              onDelete={(id) => deleteGeneration.mutateAsync(id)}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

function RetentionControl({
  value,
  disabled,
  onChange,
}: {
  value: RetentionPeriod | undefined;
  disabled: boolean;
  onChange: (period: RetentionPeriod) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 200, backgroundColor: "#FFFFFF", borderRadius: `${RADIUS_INNER_PX}px` }}>
      <InputLabel id="retention-label">Keep my history for</InputLabel>
      <Select
        labelId="retention-label"
        label="Keep my history for"
        // `?? ""` rather than leaving it undefined: an uncontrolled-to-controlled switch when
        // the query resolves makes React warn, and the empty string renders as no selection,
        // which is the honest display while the current value is still unknown.
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {RETENTION_PERIOD_OPTIONS.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function EmptyHistory() {
  return (
    <Card className="p-8 text-center">
      <Typography variant="h6" component="p" gutterBottom>
        Nothing here yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Images you generate will appear here, newest first.
      </Typography>
    </Card>
  );
}
